// Healthcare API Service
// This service handles all healthcare compliance API calls

import { apiClient } from "./client";
import { getAuthHeaders } from "../utils/authStorage";
import type { ApiResponse } from "./client";
import type { PaginatedResponse } from "../types";
import type {
  UploadedFile,
  ProcessingJob,
  ExtractedData,
  ComplianceRule,
  DocumentType,
  ExtractionPrompt,
  ProcessingStats,
  HealthcareDashboardMetrics,
  FileUploadProgress,
  ProcessingJobFilters,
  FileFilters,
  ComplianceRuleFilters,
  DocumentTypeFormData,
  ExtractionPromptFormData,
  ExtractionPromptUpdateData,
  GlobalPromptFormData,
  ExtractedDataUpdateRequest,
  FileUploadStatus,
  JobStage,
  LinkedRuleDto,
  BatchEvaluationResponse,
  ComplianceRunTriggerResponse,
  BatchRulesTaggingResponse,
  BulkRetryFailedJobsResponse,
  DocumentRulesRetaggingTriggerResponse,
  DocumentRuleSynthesisTriggerResponse,
  JobDocumentProcessingResponse,
  JobApiCallsResponse,
} from "../types/healthcare";
import type { TriggerComplianceEvaluationRequest } from "./rules";
import { CompliancePipelineStage } from "../types/healthcare";

// ================================
// File Management API
// ================================

export class HealthcareFilesApi {
  /**
   * Step 1: Create document and get presigned upload URL
   * POST /api/document-management/documents
   */
  async getPresignedUrl(
    fileName: string,
    contentType: string,
    sizeBytes: number,
    documentType?: string | null,
  ): Promise<
    ApiResponse<{
      documentId: number;
      uploadUrl: string;
      s3Bucket: string;
      s3Key: string;
      filename: string;
      contentType: string;
      expiresIn: number;
    }>
  > {
    return await apiClient.post("/document-management/documents", {
      fileName,
      contentType,
      sizeBytes,
      documentType,
    });
  }

  /**
   * Step 2: Upload file to S3 using presigned URL
   */
  async uploadToS3(
    presignedUrl: string,
    file: File,
    contentType: string,
    onProgress?: (progress: number) => void,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && onProgress) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(
            new Error(
              `S3 upload failed with status ${xhr.status}: ${xhr.statusText}`,
            ),
          );
        }
      });

      xhr.addEventListener("error", () => {
        reject(
          new Error(
            `Network error during S3 upload. This is typically a CORS configuration issue. Check S3 bucket CORS settings.`,
          ),
        );
      });

      xhr.addEventListener("abort", () => {
        reject(new Error("S3 upload was aborted"));
      });

      xhr.open("PUT", presignedUrl);
      // Only set Content-Type, let browser handle other headers
      xhr.setRequestHeader("Content-Type", contentType);
      xhr.send(file);
    });
  }

  /**
   * Step 3: Complete the upload and trigger processing
   * PATCH /api/document-management/documents/{id}/complete
   *
   * @param documentId - Document ID from step 1
   * @param sizeBytes - File size in bytes
   * @param uploadOnly - When true, only stores file and triggers rules tagging (no compliance/extraction).
   *                     When false or omitted, triggers full compliance processing.
   */
  async completeUpload(
    documentId: number,
    sizeBytes?: number,
    uploadOnly?: boolean,
  ): Promise<
    ApiResponse<{
      success: boolean;
      documentId: number;
      jobId: number;
      message: string;
      s3Key: string;
      s3Bucket: string;
      duplicate: boolean;
      existingDocumentId?: number;
    }>
  > {
    const body: Record<string, unknown> = { sizeBytes };
    if (uploadOnly !== undefined) {
      body.uploadOnly = uploadOnly;
    }
    return await apiClient.patch(
      `/document-management/documents/${documentId}/complete`,
      body,
    );
  }

  /**
   * Upload multiple files using the three-step document upload flow
   * 1. POST /api/document-management/documents - Create document and get presigned S3 URL
   * 2. PUT <presigned-url> - Upload file to S3 directly
   * 3. PATCH /api/document-management/documents/{id}/complete - Mark upload as complete and trigger processing
   *
   * Required entitlement: Document.Create
   *
   * @param files - Files to upload
   * @param onProgress - Progress callback
   * @param uploadOnly - When true, only stores file and triggers rules tagging (no compliance/extraction)
   */
  async uploadFiles(
    files: File[],
    onProgress?: (progress: FileUploadProgress[]) => void,
    uploadOnly?: boolean,
  ): Promise<ApiResponse<UploadedFile[]>> {
    const uploadedFiles: UploadedFile[] = [];
    const progressTracking: FileUploadProgress[] = files.map((file) => ({
      file,
      progress: 0,
      status: "uploading" as FileUploadStatus,
    }));

    for (let index = 0; index < files.length; index++) {
      const file = files[index];

      try {
        // Update status
        progressTracking[index].status = "uploading";
        if (onProgress) onProgress([...progressTracking]);

        // Step 1: Get presigned URL
        const presignResponse = await this.getPresignedUrl(
          file.name,
          file.type || "application/octet-stream",
          file.size,
          null,
        );

        if (!presignResponse.success || !presignResponse.data) {
          throw new Error(`Failed to get presigned URL for ${file.name}`);
        }

        const { documentId, uploadUrl, s3Key } = presignResponse.data;

        // Step 2: Upload to S3 with progress tracking
        await this.uploadToS3(
          uploadUrl,
          file,
          file.type || "application/octet-stream",
          (progress) => {
            progressTracking[index].progress = progress;
            if (onProgress) onProgress([...progressTracking]);
          },
        );

        // Step 3: Complete upload
        const completeResponse = await this.completeUpload(
          documentId,
          file.size,
          uploadOnly,
        );

        if (!completeResponse.success || !completeResponse.data) {
          const backendMessage =
            completeResponse.errors?.join(", ") ||
            completeResponse.message ||
            `Failed to complete upload for ${file.name}`;
          throw new Error(backendMessage);
        }

        // Mark as completed
        progressTracking[index].progress = 100;
        progressTracking[index].status = "completed";
        if (onProgress) onProgress([...progressTracking]);

        // Create uploaded file record
        uploadedFiles.push({
          id: documentId,
          filename: s3Key.split("/").pop() || file.name,
          originalName: file.name,
          fileSize: file.size,
          uploadTimestamp: new Date().toISOString(),
          mimeType: file.type,
          userId: "current-user",
          filePath: s3Key,
          status: "Ready",
        });
      } catch (error) {
        // Mark only this file as error with its specific message
        progressTracking[index].status = "error";
        progressTracking[index].error = (error as Error).message;
        if (onProgress) onProgress([...progressTracking]);
      }
    }

    return {
      success: uploadedFiles.length > 0,
      data: uploadedFiles,
      message:
        uploadedFiles.length === files.length
          ? "Files uploaded successfully"
          : `${uploadedFiles.length} of ${files.length} files uploaded successfully`,
    };
  }

  /**
   * Get all uploaded files with pagination and filtering
   * Uses document-management API endpoint for listing uploaded documents
   */
  async getAllFiles(
    page: number = 0,
    size: number = 10,
    filters?: FileFilters,
  ): Promise<ApiResponse<PaginatedResponse<UploadedFile>>> {
    interface ApiDocument {
      id: number;
      type: string | null;
      filename: string | null;
      originalFilename: string;
      filePath: string | null;
      fileSize: number | null;
      mimeType: string | null;
      uploadedAt: string | null;
      compliancePipelineStage?: string | null;
    }

    interface DocumentPageResponse {
      data: ApiDocument[];
      page: number;
      pageSize: number;
      totalElements: number;
      totalPages: number;
    }

    const {
      compliancePipelineStage,
      compliancePipelineStages,
      ...restFilters
    } = filters ?? {};

    const stageParams: Record<string, string | number> = {};
    if (compliancePipelineStages?.length) {
      stageParams.compliancePipelineStages = compliancePipelineStages.join(",");
    } else if (compliancePipelineStage != null) {
      stageParams.compliancePipelineStage = compliancePipelineStage;
    }

    const response = await apiClient.get<DocumentPageResponse>(
      "/document-management/documents",
      {
        params: {
          page: page + 1, // Convert 0-based to 1-based
          size,
          sortBy: "uploadedAt",
          sortDirection: "DESC",
          ...restFilters,
          ...stageParams,
        },
      },
    );

    // Transform API response to frontend format
    if (response.success && response.data) {
      const apiData = response.data;

      // Map API documents to frontend UploadedFile format
      const transformedFiles: UploadedFile[] = (apiData.data || []).map(
        (doc) => ({
          id: doc.id,
          filename: doc.filename || doc.originalFilename,
          originalName: doc.originalFilename,
          fileSize: doc.fileSize || 0,
          uploadTimestamp: doc.uploadedAt || new Date().toISOString(),
          mimeType: doc.mimeType || "application/octet-stream",
          userId: "unknown", // API doesn't return user info
          filePath: doc.filePath || "",
          status: "Ready",
          compliancePipelineStage: (
            Object.values(CompliancePipelineStage) as string[]
          ).includes(doc.compliancePipelineStage ?? "")
            ? (doc.compliancePipelineStage as CompliancePipelineStage)
            : null,
        }),
      );

      const transformedData: PaginatedResponse<UploadedFile> = {
        items: transformedFiles,
        totalCount: apiData.totalElements || 0,
        pageNumber: apiData.page || 1, // API uses 1-based pagination
        pageSize: apiData.pageSize || size,
        totalPages: apiData.totalPages || 1,
        hasNextPage: (apiData.page || 1) < (apiData.totalPages || 1),
        hasPreviousPage: (apiData.page || 1) > 1,
      };

      return {
        ...response,
        data: transformedData,
      };
    }

    return {
      success: false,
      data: null as unknown as PaginatedResponse<UploadedFile>,
      message: "Failed to retrieve documents",
    };
  }

  /**
   * Download/Preview a file
   * GET /api/document-management/documents/{id} - Returns document details including URL
   */
  async downloadFile(fileId: number): Promise<string> {
    // Get document details which includes the URL
    const response = await apiClient.get<{
      id: string;
      fileName: string;
      contentType: string;
      sizeBytes: number;
      status: string;
      documentType: string;
      uploadedAt: string;
      uploadedBy: string;
      pageCount: number;
      ocrReadiness: string;
      priority: string;
      criticalityTags: string[];
      sourceSystem: string;
      errorMessage: string;
      url?: string; // The document URL for download/preview
    }>(`/document-management/documents/${fileId}`);

    if (response.success && response.data?.url) {
      // Return the URL from document details
      return response.data.url;
    }

    return "";
  }

  /**
   * Get a presigned download URL for a document (without downloading the file).
   * GET /api/document-management/documents/{id}/download
   *
   * Useful for passing directly to canvas-based viewers (e.g. PdfViewer)
   * where an iframe/embed is blocked by CSP.
   *
   * @param documentId - The document ID
   * @returns The presigned S3 download URL
   */
  async getDocumentPreviewUrl(documentId: number): Promise<string> {
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}/document-management/documents/${documentId}/download`,
      {
        method: "GET",
        credentials: "include" as RequestCredentials,
        headers: { ...getAuthHeaders() },
      },
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized: Please log in again");
      }
      if (response.status === 404) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Document not found" }));
        throw new Error(errorData.error || "Document not found");
      }
      throw new Error(
        `Download failed (${response.status}): ${response.statusText || "The file could not be downloaded. Please try again."}`,
      );
    }

    const jsonResponse = await response.json();
    const downloadUrl =
      jsonResponse.data?.downloadUrl || jsonResponse.downloadUrl;

    if (!downloadUrl) {
      throw new Error("Download URL not available");
    }

    return downloadUrl;
  }

  /**
   * Download document via presigned URL
   * GET /api/document-management/documents/{id}/download
   *
   * Returns the file content as a Blob by fetching from the presigned S3 URL.
   * The API returns a JSON with downloadUrl, which we then fetch to get the actual file.
   *
   * @param documentId - The document ID to download
   * @returns Promise<{ blob: Blob, filename: string }> - File blob and extracted filename
   * @throws Error if download fails or document not found
   */
  async downloadDocument(
    documentId: number,
    knownMimeType?: string,
  ): Promise<{ blob: Blob; filename: string }> {
    // Step 1: Get the presigned download URL from the API
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}/document-management/documents/${documentId}/download`,
      {
        method: "GET",
        credentials: "include" as RequestCredentials,
        headers: { ...getAuthHeaders() },
      },
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized: Please log in again");
      }
      if (response.status === 404) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Document not found" }));
        throw new Error(errorData.error || "Document not found");
      }
      throw new Error(
        `Download failed (${response.status}): ${response.statusText || "The file could not be downloaded. Please try again."}`,
      );
    }

    // Parse the JSON response to get the presigned URL
    const jsonResponse = await response.json();
    const downloadUrl =
      jsonResponse.data?.downloadUrl || jsonResponse.downloadUrl;

    if (!downloadUrl) {
      throw new Error("Download URL not available");
    }

    // Step 2: Fetch the actual file from the presigned S3 URL
    const fileResponse = await fetch(downloadUrl, {
      method: "GET",
      mode: "cors",
    });
    if (!fileResponse.ok) {
      throw new Error(
        `Failed to download file (${fileResponse.status}): ${fileResponse.statusText || "Please try again."}`,
      );
    }

    // Extract filename from Content-Disposition header if available,
    // otherwise fall back to the original filename from the API response.
    const contentDisposition = fileResponse.headers.get("Content-Disposition");
    let filename = "";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+?)"?$/);
      if (match) {
        filename = match[1];
      }
    }
    if (!filename) {
      filename =
        jsonResponse.data?.originalFilename ||
        jsonResponse.data?.filename ||
        jsonResponse.originalFilename ||
        jsonResponse.filename ||
        `document-${documentId}`;
    }

    // Get content type: prefer known mime type, then response header, then fallback
    const contentType =
      knownMimeType ||
      fileResponse.headers.get("Content-Type") ||
      "application/octet-stream";

    // Get file content as blob with explicit type
    const arrayBuffer = await fileResponse.arrayBuffer();
    const blob = new Blob([arrayBuffer], { type: contentType });
    return { blob, filename };
  }

  /**
   * Soft delete a document
   * DELETE /api/document-management/documents/{id}
   *
   * Soft deletes a document by marking it as inactive. The document will no longer
   * appear in GET endpoints, but the S3 file and database record remain intact.
   *
   * @param fileId - Document ID to delete
   * @returns Success/error response
   */
  async deleteFile(fileId: number): Promise<ApiResponse<void>> {
    return await apiClient.delete<void>(
      `/document-management/documents/${fileId}`,
    );
  }

  /**
   * Alias for deleteFile - soft delete a document
   * DELETE /api/document-management/documents/{id}
   */
  async deleteDocument(documentId: number): Promise<ApiResponse<void>> {
    return this.deleteFile(documentId);
  }

  /**
   * Get rules linked to a document (via rules tagging)
   * GET /api/document-management/documents/{id}/linked-rules
   */
  async getDocumentLinkedRules(
    documentId: number,
  ): Promise<ApiResponse<LinkedRuleDto[]>> {
    return await apiClient.get<LinkedRuleDto[]>(
      `/document-management/documents/${documentId}/linked-rules`,
    );
  }

  /**
   * Trigger full compliance run for all active tenant rules
   * POST /api/document-management/documents/compliance-run/trigger
   */
  async triggerComplianceRun(
    body?: TriggerComplianceEvaluationRequest,
  ): Promise<ApiResponse<ComplianceRunTriggerResponse>> {
    return await apiClient.post<ComplianceRunTriggerResponse>(
      "/document-management/documents/compliance-run/trigger",
      body,
    );
  }

  /**
   * Trigger two-phase rule synthesis (Phase B).
   * First call with force=false; re-call with force=true if the response warns of pending Phase A jobs.
   * POST /api/document-management/documents/document-rule-synthesis/trigger?force={force}
   */
  async triggerDocumentRuleSynthesis(
    body?: TriggerComplianceEvaluationRequest,
    force = false,
  ): Promise<ApiResponse<DocumentRuleSynthesisTriggerResponse>> {
    return await apiClient.post<DocumentRuleSynthesisTriggerResponse>(
      `/document-management/documents/document-rule-synthesis/trigger?force=${force}`,
      body,
    );
  }

  /**
   * Trigger batch rules tagging for all tenant documents
   * POST /api/document-management/documents/batch-rules-tagging
   */
  async triggerBatchRulesTagging(): Promise<
    ApiResponse<BatchRulesTaggingResponse>
  > {
    return await apiClient.post<BatchRulesTaggingResponse>(
      "/document-management/documents/batch-rules-tagging",
    );
  }

  async retriggerDocumentRulesTagging(
    documentId: number,
  ): Promise<ApiResponse<DocumentRulesRetaggingTriggerResponse>> {
    return await apiClient.post<DocumentRulesRetaggingTriggerResponse>(
      `/document-management/documents/${documentId}/rules-tagging/retrigger`,
    );
  }

  /**
   * Trigger compliance evaluation for a single document
   * POST /api/document-management/documents/{id}/compliance-evaluation/trigger
   */
  async triggerSingleDocumentComplianceEvaluation(
    documentId: number,
  ): Promise<ApiResponse<BatchEvaluationResponse>> {
    return await apiClient.post<BatchEvaluationResponse>(
      `/document-management/documents/${documentId}/compliance-evaluation/trigger`,
    );
  }
}

// ================================
// Processing API
// ================================

export class HealthcareProcessingApi {
  /**
   * Start processing a file
   */
  async startProcessing(fileId: number): Promise<ApiResponse<ProcessingJob>> {
    return await apiClient.post<ProcessingJob>(
      `/healthcare/processing/start/${fileId}`,
    );
  }

  /**
   * Get processing status for a job
   * GET /api/job-management/jobs/{id}
   */
  async getProcessingStatus(
    jobId: number,
  ): Promise<ApiResponse<ProcessingJob>> {
    return await apiClient.get<ProcessingJob>(`/job-management/jobs/${jobId}`);
  }

  /**
   * Get all processing jobs with filtering
   * GET /api/job-management/jobs
   */
  async getAllProcessingJobs(
    page: number = 1,
    size: number = 20,
    filters?: ProcessingJobFilters,
    jobTypes?: string[],
  ): Promise<ApiResponse<PaginatedResponse<ProcessingJob>>> {
    const params: Record<string, string | number> = {
      page,
      pageSize: size,
      sortBy: "id",
      sortDirection: "DESC",
    };

    // Add filters
    if (filters?.status?.length) {
      params.status = filters.status.join(",");
    }
    if (filters?.searchText) params.searchText = filters.searchText;
    if (filters?.documentType) params.documentType = filters.documentType;
    if (filters?.createdAt) params.createdAt = filters.createdAt;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;

    // Add job types filter if specified
    if (jobTypes?.length) {
      params.jobTypes = jobTypes.join(",");
    }

    // Single API call to get all jobs
    const response = await apiClient.get<PaginatedResponse<ProcessingJob>>(
      `/job-management/jobs`,
      { params },
    );

    // API returns nested 'data' field with pagination info
    const allJobs: ProcessingJob[] =
      response.data?.data ||
      response.data?.items ||
      response.data?.content ||
      [];

    return {
      success: true,
      data: {
        content: allJobs,
        items: allJobs, // Support both field names
        data: allJobs, // Support new nested data field
        number: response.data?.page || response.data?.number || page,
        size: response.data?.pageSize || response.data?.size || size,
        totalElements: response.data?.totalElements || 0,
        totalPages: response.data?.totalPages || 0,
      },
    };
  }

  /**
   * Get processing jobs by status
   */
  async getProcessingJobsByStatus(
    status: string,
  ): Promise<ApiResponse<ProcessingJob[]>> {
    return await apiClient.get<ProcessingJob[]>(
      `/document-management/jobs/type/DOCUMENT_PROCESSING`,
      {
        params: { status },
      },
    );
  }

  /**
   * Get processing statistics
   */
  async getProcessingStats(): Promise<ApiResponse<ProcessingStats>> {
    return await apiClient.get<ProcessingStats>("/job-management/jobs/stats");
  }

  /**
   * Get jobs for a specific document
   * GET /api/job-management/jobs (filtered by documentId)
   */
  async getJobsByDocumentId(
    documentId: number,
    page: number = 0,
    size: number = 10,
  ): Promise<ApiResponse<PaginatedResponse<ProcessingJob>>> {
    return await apiClient.get<PaginatedResponse<ProcessingJob>>(
      `/job-management/jobs`,
      {
        params: { page, size, documentId },
      },
    );
  }

  /**
   * Get stages for a specific job
   * GET /api/job-management/jobs/{id}/stages
   *
   * Returns all processing stages for a job with their status and timing information.
   * Note: stageData is returned as a JSON string from the API and needs to be parsed.
   *
   * @param jobId - Job ID to get stages for
   * @returns Array of JobStage objects
   */
  async getJobStages(jobId: number): Promise<ApiResponse<JobStage[]>> {
    const response = await apiClient.get<JobStage[]>(
      `/job-management/jobs/${jobId}/stages`,
    );

    // Parse stageData JSON strings into objects
    if (response.success && response.data && Array.isArray(response.data)) {
      response.data = response.data.map((stage) => ({
        ...stage,
        // Parse stageData if it's a string
        stageData:
          typeof stage.stageData === "string"
            ? JSON.parse(stage.stageData)
            : stage.stageData,
        // Also set data alias for backward compatibility
        data:
          typeof stage.stageData === "string"
            ? JSON.parse(stage.stageData)
            : stage.stageData,
      }));
    }

    return response;
  }

  /**
   * Retry a failed job
   * POST /api/job-management/jobs/{id}/retry
   *
   * Retries a failed document processing job by:
   * 1. Validating job status is FAILED
   * 2. Checking retry count hasn't exceeded max retries
   * 3. Creating new job with QUEUED status
   * 4. Re-enqueuing message to restart workflow
   *
   * @param jobId - Job ID to retry
   * @returns Success message with updated status
   */
  async retryJob(jobId: number): Promise<ApiResponse<ProcessingJob>> {
    return await apiClient.post<ProcessingJob>(
      `/job-management/jobs/${jobId}/retry`,
    );
  }

  /**
   * Bulk retry failed jobs for the current tenant
   * POST /api/job-management/jobs/retry-failed
   *
   * Each job is processed independently; partial success is normal.
   * Inspect response.failures for per-job errors.
   */
  async bulkRetryFailedJobs(options?: {
    includeQueued?: boolean;
    jobTypes?: string[];
    limit?: number;
  }): Promise<ApiResponse<BulkRetryFailedJobsResponse>> {
    const params = new URLSearchParams();
    if (options?.includeQueued) params.set("includeQueued", "true");
    if (options?.jobTypes?.length)
      params.set("jobTypes", options.jobTypes.join(","));
    if (options?.limit != null) params.set("limit", String(options.limit));
    const qs = params.toString();
    return await apiClient.post<BulkRetryFailedJobsResponse>(
      `/job-management/jobs/retry-failed${qs ? `?${qs}` : ""}`,
    );
  }

  async getDocumentProcessing(
    jobId: number,
  ): Promise<ApiResponse<JobDocumentProcessingResponse>> {
    return await apiClient.get<JobDocumentProcessingResponse>(
      `/job-management/jobs/${jobId}/document-processing`,
    );
  }

  async getDocumentApiCalls(
    jobId: number,
    documentId: number,
    callType?: "LLM" | "OCR",
  ): Promise<ApiResponse<JobApiCallsResponse>> {
    const qs = callType ? `?callType=${callType}` : "";
    return await apiClient.get<JobApiCallsResponse>(
      `/job-management/jobs/${jobId}/documents/${documentId}/api-calls${qs}`,
    );
  }
}

// ================================
// Extracted Data API
// ================================

export class HealthcareExtractionApi {
  /**
   * Get extracted data for a processing job
   */
  async getExtractedData(jobId: number): Promise<ApiResponse<ExtractedData[]>> {
    return await apiClient.get<ExtractedData[]>(
      `/healthcare/extraction/${jobId}`,
    );
  }

  /**
   * Update extracted data field
   */
  async updateExtractedData(
    dataId: number,
    update: ExtractedDataUpdateRequest,
  ): Promise<ApiResponse<ExtractedData>> {
    return await apiClient.put<ExtractedData>(
      `/healthcare/extraction/${dataId}`,
      update,
    );
  }

  /**
   * Export extracted data
   */
  async exportExtractedData(
    jobId: number,
    format: "csv" | "json",
  ): Promise<Blob> {
    const response = await apiClient.get(
      `/healthcare/extraction/export/${jobId}/${format}`,
      {
        responseType: "blob",
      },
    );
    return response.data as Blob;
  }
}

// ================================
// Compliance Rules API
// ================================

export class HealthcareComplianceApi {
  /**
   * Get all compliance rules
   */
  async getAllComplianceRules(
    filters?: ComplianceRuleFilters,
  ): Promise<ApiResponse<ComplianceRule[]>> {
    return await apiClient.get<ComplianceRule[]>(
      "/healthcare/compliance-rules/all",
      {
        params: filters as Record<string, unknown>,
      },
    );
  }

  /**
   * Get compliance rule by ID
   */
  async getComplianceRule(
    ruleId: number,
  ): Promise<ApiResponse<ComplianceRule>> {
    return await apiClient.get<ComplianceRule>(
      `/healthcare/compliance-rules/${ruleId}`,
    );
  }
}

// ================================
// Document Types API
// ================================

export class HealthcareDocumentTypesApi {
  /**
   * List Document Types (with pagination and filtering)
   * GET /api/document-management/document-types
   * @param page - Page number (0-based, default: 0)
   * @param size - Page size (default: 10)
   * @param sortBy - Sort field (default: "name")
   * @param sortDir - Sort direction "asc" or "desc" (default: "asc")
   * @param tenantOnly - Include only tenant-specific types (default: false)
   */
  async getAllDocumentTypes(
    page: number = 0,
    size: number = 10,
    sortBy: string = "name",
    sortDir: "asc" | "desc" = "asc",
    tenantOnly: boolean = false,
    name?: string,
  ): Promise<ApiResponse<PaginatedResponse<DocumentType>>> {
    interface BackendPaginatedResponse {
      data: DocumentType[];
      page: number;
      pageSize: number;
      totalElements: number;
      totalPages: number;
    }

    const response = await apiClient.get<BackendPaginatedResponse>(
      "/document-management/document-types",
      {
        params: {
          page,
          size,
          sortBy,
          sortDir,
          tenantOnly,
          ...(name && { name }),
        },
      },
    );

    // Transform backend pagination format to frontend format
    // Note: apiClient.get already unwraps one level, so response.data is the actual data object
    if (response.success && response.data) {
      const backendData = response.data;
      const transformedData: PaginatedResponse<DocumentType> = {
        items: backendData.data || [],
        totalCount: backendData.totalElements || 0,
        pageNumber: (backendData.page || 0) + 1, // Backend uses 0-based, frontend uses 1-based
        pageSize: backendData.pageSize || size,
        totalPages: backendData.totalPages || 1,
        hasNextPage:
          (backendData.page || 0) < (backendData.totalPages || 1) - 1,
        hasPreviousPage: (backendData.page || 0) > 0,
      };

      return {
        ...response,
        data: transformedData,
      };
    }

    return {
      success: false,
      data: null as unknown as PaginatedResponse<DocumentType>,
      message: "Failed to retrieve document types",
    };
  }

  /**
   * Get Document Type by ID
   * GET /api/document-management/document-types/{id}
   */
  async getDocumentType(id: number): Promise<ApiResponse<DocumentType>> {
    const response = await apiClient.get<DocumentType>(
      `/document-management/document-types/${id}`,
    );

    if (response.success && response.data) {
      return response;
    }

    return {
      success: false,
      data: null as unknown as DocumentType,
      message: "Failed to retrieve document type",
    };
  }

  /**
   * Create Document Type
   * POST /api/document-management/document-types
   * JWT authentication automatically extracts user info for audit trails
   */
  async createDocumentType(
    data: DocumentTypeFormData,
  ): Promise<ApiResponse<DocumentType>> {
    const response = await apiClient.post<DocumentType>(
      "/document-management/document-types",
      data,
    );

    if (response.success && response.data) {
      return response;
    }

    return {
      success: false,
      data: null as unknown as DocumentType,
      message: "Failed to create document type",
    };
  }

  /**
   * Update Document Type
   * PUT /api/document-management/document-types/{id}
   * JWT authentication automatically extracts user info for audit trails
   */
  async updateDocumentType(
    id: number,
    data: Partial<DocumentTypeFormData>,
  ): Promise<ApiResponse<DocumentType>> {
    const response = await apiClient.put<DocumentType>(
      `/document-management/document-types/${id}`,
      data,
    );

    if (response.success && response.data) {
      return response;
    }

    return {
      success: false,
      data: null as unknown as DocumentType,
      message: "Failed to update document type",
    };
  }

  /**
   * Delete Document Type
   * DELETE /api/document-management/document-types/{id}
   */
  async deleteDocumentType(
    id: number,
  ): Promise<ApiResponse<{ message: string }>> {
    return await apiClient.delete<{ message: string }>(
      `/document-management/document-types/${id}`,
    );
  }
}

// ================================
// Extraction Prompts API
// ================================

export class HealthcarePromptsApi {
  /**
   * Get prompts with pagination
   * GET /api/prompt-management/prompts
   */
  async getAllPrompts(params?: {
    tenantId?: number | null;
    jobType?: string;
    searchText?: string;
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: "asc" | "desc";
  }): Promise<ApiResponse<PaginatedResponse<ExtractionPrompt>>> {
    const queryParams = new URLSearchParams();
    // Only include tenantId when it's a number (not null/undefined) - null = Global for admins
    if (params?.tenantId !== undefined && params?.tenantId !== null) {
      queryParams.append("tenantId", params.tenantId.toString());
    }
    if (params?.jobType) queryParams.append("jobType", params.jobType);
    if (params?.searchText) queryParams.append("searchText", params.searchText);
    if (params?.page !== undefined)
      queryParams.append("page", params.page.toString());
    if (params?.size !== undefined)
      queryParams.append("size", params.size.toString());
    if (params?.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params?.sortDir) queryParams.append("sortDir", params.sortDir);

    const queryString = queryParams.toString();
    const url = `/prompt-management/prompts${queryString ? `?${queryString}` : ""}`;

    // apiClient.get unwraps outer layer automatically
    const response = await apiClient.get<{
      data: ExtractionPrompt[];
      page: number;
      pageSize: number;
      totalElements: number;
      totalPages: number;
    }>(url);

    if (response.success && response.data) {
      const backendData = response.data;
      const transformedData: PaginatedResponse<ExtractionPrompt> = {
        content: backendData.data || [],
        number: backendData.page || 0,
        size: backendData.pageSize || 20,
        totalElements: backendData.totalElements || 0,
        totalPages: backendData.totalPages || 0,
        first: (backendData.page || 0) === 0,
        last: (backendData.page || 0) >= (backendData.totalPages || 1) - 1,
      };
      return { success: true, data: transformedData };
    }

    return response as ApiResponse<PaginatedResponse<ExtractionPrompt>>;
  }

  /**
   * Get specific prompt by ID
   * GET /api/prompt-management/prompts/{id}
   */
  async getPromptById(id: number): Promise<ApiResponse<ExtractionPrompt>> {
    return await apiClient.get<ExtractionPrompt>(
      `/prompt-management/prompts/${id}`,
    );
  }

  /**
   * Create tenant-specific prompt
   * POST /api/prompt-management/prompts
   */
  async createExtractionPrompt(
    data: ExtractionPromptFormData,
  ): Promise<ApiResponse<ExtractionPrompt>> {
    const response = await apiClient.post<ExtractionPrompt>(
      "/prompt-management/prompts",
      data,
    );
    return response;
  }

  /**
   * Update extraction prompt
   * PUT /api/prompt-management/prompts/{id}
   */
  async updateExtractionPrompt(
    id: number,
    data: ExtractionPromptUpdateData,
  ): Promise<ApiResponse<ExtractionPrompt>> {
    const response = await apiClient.put<ExtractionPrompt>(
      `/prompt-management/prompts/${id}`,
      data,
    );
    return response;
  }

  /**
   * Delete extraction prompt
   * DELETE /api/prompt-management/prompts/{id}
   */
  async deleteExtractionPrompt(id: number): Promise<ApiResponse<void>> {
    const response = await apiClient.delete<void>(
      `/prompt-management/prompts/${id}`,
    );
    return response;
  }

  /**
   * Super Admin: Get all global prompts
   * GET /api/prompt-management/prompts/admin/global-prompts
   */
  async getGlobalPrompts(
    jobType?: string,
  ): Promise<ApiResponse<ExtractionPrompt[]>> {
    const queryString = jobType
      ? `?jobType=${encodeURIComponent(jobType)}`
      : "";
    return await apiClient.get<ExtractionPrompt[]>(
      `/prompt-management/prompts/admin/global-prompts${queryString}`,
    );
  }

  /**
   * Super Admin: Create global prompt
   * POST /api/prompt-management/prompts/admin/global-prompts
   */
  async createGlobalPrompt(
    data: GlobalPromptFormData,
  ): Promise<ApiResponse<ExtractionPrompt>> {
    return await apiClient.post<ExtractionPrompt>(
      "/prompt-management/prompts/admin/global-prompts",
      data,
    );
  }

  /**
   * Get tenant-specific prompts only
   * GET /api/prompt-management/prompts/tenant-isolation/{tenantId}
   */
  async getTenantPrompts(
    tenantId: number,
    jobType?: string,
  ): Promise<ApiResponse<ExtractionPrompt[]>> {
    const queryString = jobType
      ? `?jobType=${encodeURIComponent(jobType)}`
      : "";
    return await apiClient.get<ExtractionPrompt[]>(
      `/prompt-management/prompts/tenant-isolation/${tenantId}${queryString}`,
    );
  }
}

// ================================
// Dashboard API
// ================================

export class HealthcareDashboardApi {
  /**
   * Get dashboard metrics
   */
  async getDashboardMetrics(): Promise<
    ApiResponse<HealthcareDashboardMetrics>
  > {
    return await apiClient.get<HealthcareDashboardMetrics>(
      "/healthcare/dashboard/metrics",
    );
  }
}

// ================================
// Export API Instances
// ================================

export const healthcareFilesApi = new HealthcareFilesApi();
export const healthcareProcessingApi = new HealthcareProcessingApi();
export const healthcareExtractionApi = new HealthcareExtractionApi();
export const healthcareComplianceApi = new HealthcareComplianceApi();
export const healthcareDocumentTypesApi = new HealthcareDocumentTypesApi();
export const healthcarePromptsApi = new HealthcarePromptsApi();
export const healthcareDashboardApi = new HealthcareDashboardApi();

// ================================
// Main Healthcare API Class
// ================================

export class HealthcareApi {
  files = healthcareFilesApi;
  processing = healthcareProcessingApi;
  extraction = healthcareExtractionApi;
  compliance = healthcareComplianceApi;
  documentTypes = healthcareDocumentTypesApi;
  prompts = healthcarePromptsApi;
  dashboard = healthcareDashboardApi;
}

// Export main API instance
export const healthcareApi = new HealthcareApi();

// Export individual APIs for direct use
export default healthcareApi;
